import React, { useRef, useState, useEffect } from "react";
import { Button, Popover, Tooltip, message, Modal, Avatar } from "antd";
import {
    BoldOutlined,
    ItalicOutlined,
    StrikethroughOutlined,
    LinkOutlined,
    OrderedListOutlined,
    UnorderedListOutlined,
    CodeOutlined,
    SmileOutlined,
    PaperClipOutlined,
    SendOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import "./ChatEditor.css";

// richer user data used for mention list (id, name, avatar)

const emojis = ["😀", "😂", "😍", "😎", "🔥", "🎉", "🚀", "👍", "🙏", "💡"];

export default function ChatEditor({
    onRichTextSaved,
    onSend,
    users,
    showTools,
    prefill = "",
    editMode,
    onCancel,
    editMessageId,
    editAttachments,
    isRichTextField = false,
    placeholder = "Add a comment...",
}) {
    const editorRef = useRef(null);
    const wrapperRef = useRef(null);
    const fileInputRef = useRef(null);
    // keep last known caret range inside editor so popover clicks don't lose insertion point
    const lastRangeRef = useRef(null);
    const [showEditors, setShowEditors] = useState(showTools);


    const [showMentions, setShowMentions] = useState(false);
    const [mentionCandidates, setMentionCandidates] = useState([]);
    const [mentionQuery, setMentionQuery] = useState("");
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    // Attachments: { filename, base64code }
    const [attachments, setAttachments] = useState([]);
    const attachmentsRef = useRef(attachments);


    useEffect(() => {
        attachmentsRef.current = attachments;
    }, [attachments]);
    const [previewModal, setPreviewModal] = useState({ visible: false, content: null, type: null });
    const [showEmojiPopover, setShowEmojiPopover] = useState(false);
    const [commentText, setCommentText] = useState(prefill || "");
    // Prefill message on mount or when prefill changes
    useEffect(() => {
        if (editorRef.current && prefill) {
            editorRef.current.innerHTML = prefill;
            setCommentText(editorRef.current.innerText || "");
        }
    }, [prefill]);

    useEffect(() => {
        if (editorRef.current && prefill !== undefined) {
            editorRef.current.innerHTML = prefill || "";

            // Set comment text
            setCommentText(editorRef.current.innerText || "");

            // 👇 Ensure caret is placed at the end after prefill is loaded
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(editorRef.current);
            range.collapse(false); // false = move cursor to end
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }, [prefill]);


    // track active formats to highlight toolbar buttons
    const [activeFormats, setActiveFormats] = useState({
        bold: false,
        italic: false,
        strike: false,
        unordered: false,
        ordered: false,
        code: false,
        quote: false,
    });

    // focused state for editor UI (Slack-like highlight)
    const [focused, setFocused] = useState(false);

    // helper to detect ancestor tag names (for code/blockquote detection)
    const nodeHasAncestorTag = (node, tags = []) => {
        let n = node;
        while (n && n !== editorRef.current && n.nodeType !== Node.DOCUMENT_NODE) {
            if (n.nodeType === Node.ELEMENT_NODE) {
                const name = n.nodeName.toLowerCase();
                if (tags.includes(name)) return true;
            }
            n = n.parentNode;
        }
        return false;
    };

    // update activeFormats based on caret/selection
    const updateActiveFormats = () => {
        try {
            const sel = window.getSelection();
            // if selection is not inside editor, clear state (prevents toolbar falsely showing active)
            if (!sel || !sel.anchorNode || !editorRef.current || !editorRef.current.contains(sel.anchorNode)) {
                setActiveFormats({
                    bold: false,
                    italic: false,
                    strike: false,
                    unordered: false,
                    ordered: false,
                    code: false,
                    quote: false,
                });
                return;
            }

            const bold = document.queryCommandState("bold");
            const italic = document.queryCommandState("italic");
            const strike = document.queryCommandState("strikeThrough");
            const unordered = document.queryCommandState("insertUnorderedList");
            const ordered = document.queryCommandState("insertOrderedList");

            let code = nodeHasAncestorTag(sel.anchorNode, ["code", "pre"]);
            let quote = nodeHasAncestorTag(sel.anchorNode, ["blockquote"]);

            // save last range while selection is inside editor
            try {
                if (sel.rangeCount) lastRangeRef.current = sel.getRangeAt(0).cloneRange();
            } catch (err) {
                // ignore
            }

            setActiveFormats({ bold, italic, strike, unordered, ordered, code, quote });
        } catch (err) {
            // ignore
        }
    };

    // UTIL: execute simple formatting commands
    const exec = (command, value = null) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
        // update the toolbar state after the command
        setTimeout(updateActiveFormats, 0);
    };

    // Toolbar handlers
    const applyBold = () => exec("bold");
    const applyItalic = () => exec("italic");
    const applyStrike = () => exec("strikeThrough");
    const applyUnordered = () => exec("insertUnorderedList");
    const applyOrdered = () => exec("insertOrderedList");
    // toggle block code (pre > code). If selection already inside pre -> unwrap.
    // Otherwise insert an empty pre>code on the next line (after the current block) and focus it.
    const applyCode = () => {
        const sel = window.getSelection();
        const anchor = sel && sel.anchorNode;

        // detect existing pre ancestor
        let n = anchor;
        let preAncestor = null;
        while (n && n !== editorRef.current) {
            if (n.nodeType === Node.ELEMENT_NODE && n.nodeName.toLowerCase() === "pre") {
                preAncestor = n;
                break;
            }
            n = n.parentNode;
        }

        if (preAncestor) {
            // unwrap: keep content visible, then place caret after the block
            const parent = preAncestor.parentNode;
            const wrapper = document.createElement("div");
            wrapper.textContent = preAncestor.innerText;
            parent.replaceChild(wrapper, preAncestor);

            const newBlock = document.createElement("div");
            newBlock.innerHTML = "<br>";
            parent.insertBefore(newBlock, wrapper.nextSibling);

            const range = document.createRange();
            range.setStart(newBlock, 0);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            editorRef.current.focus();
            setTimeout(updateActiveFormats, 0);
            return;
        }

        // insert pre>code on the next line after the current block
        // find the nearest block that is a direct child of editorRef
        let block = null;
        if (anchor) {
            let m = anchor;
            while (m && m !== editorRef.current) {
                if (m.parentNode === editorRef.current) {
                    block = m;
                    break;
                }
                m = m.parentNode;
            }
        }

        const pre = document.createElement("pre");
        const code = document.createElement("code");
        code.setAttribute("contenteditable", "true");
        code.appendChild(document.createTextNode(""));
        pre.appendChild(code);

        if (block) {
            // insert after the block
            block.parentNode.insertBefore(pre, block.nextSibling);
        } else {
            // empty editor or caret not inside a block: append at end
            editorRef.current.appendChild(pre);
        }

        // place caret inside code
        const newRange = document.createRange();
        newRange.setStart(code.firstChild, 0);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
        editorRef.current.focus();
        setTimeout(updateActiveFormats, 0);
    };
    const applyQuote = () => {
        wrapSelectionWithTag("blockquote");
        setTimeout(updateActiveFormats, 0);
    };
    const insertLink = () => {
        const url = window.prompt("Enter link URL:");
        if (url) {
            exec("createLink", url);
            setTimeout(updateActiveFormats, 0);
        }
    };

    // Wrap selection with an inline/block tag
    const wrapSelectionWithTag = (tag) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        const content = range.extractContents();
        const el = document.createElement(tag);
        el.appendChild(content);
        range.insertNode(el);
        // place caret after inserted element
        range.setStartAfter(el);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        editorRef.current.focus();
        setTimeout(updateActiveFormats, 0);
    };

    // Handle input and mention detection
    const handleInput = (e) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) {
            setShowMentions(false);
            updateActiveFormats();
            return;
        }
        const range = sel.getRangeAt(0);
        const precedingRange = range.cloneRange();
        // look back a reasonable number of chars for mention query
        precedingRange.setStart(range.startContainer, Math.max(0, range.startOffset - 30));
        const text = precedingRange.toString();
        const m = text.match(/@([a-zA-Z0-9_]*)$/);
        if (m) {
            const query = m[1];
            // filter by user.name
            const filtered = users?.filter((u) =>
                u?.Name?.toLowerCase()?.startsWith(query?.toLowerCase())
            );

            console.log('filtered ', filtered);
            setMentionCandidates(filtered);
            setMentionQuery(query);
            setShowMentions(true);
            // compute position for dropdown using the caret rect
            try {
                const caretRect = range.getBoundingClientRect();
                const wrapperRect = wrapperRef.current.getBoundingClientRect();
                setDropdownPos({
                    top: caretRect.bottom - wrapperRect.top + 6,
                    left: caretRect.left - wrapperRect.left,
                });
            } catch (err) {
                // fallback
                setDropdownPos({ top: 30, left: 10 });
            }
        } else {
            setShowMentions(false);
        }
        updateActiveFormats();


        // keep a simple plain-text copy of the editor content so we can enable/disable the Send button
        try {
            setCommentText(editorRef.current ? (editorRef.current.innerText || "") : "");
            if (isRichTextField && onRichTextSaved) {
                onRichTextSaved(editorRef.current ? (editorRef.current.innerHTML || "") : "");
            }
        } catch (err) {
            setCommentText("");
        }
    };

    // Insert mention node at caret; add a trailing space
    const insertMention = (user) => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);

        // remove the mention query (e.g., "@mu") preceding caret
        // Walk backwards to find the @
        let container = range.startContainer;
        let offset = range.startOffset;

        // If container is element, create a text node as fallback
        if (container.nodeType === Node.ELEMENT_NODE) {
            // insert at offset: create a text node caret location for easier deletion
            const txt = document.createTextNode("");
            container.insertBefore(txt, container.childNodes[offset]);
            container = txt;
            offset = 0;
            range.setStart(container, 0);
            range.setEnd(container, 0);
        }

        // Now remove chars until '@'
        let curNode = container;
        let curOffset = offset;
        // If the node is a text node, remove characters in it; otherwise bail
        if (curNode.nodeType === Node.TEXT_NODE) {
            let text = curNode.textContent || "";
            // find last '@' before offset
            const sub = text.slice(0, curOffset);
            const atIndex = sub.lastIndexOf("@");
            if (atIndex !== -1) {
                // delete from atIndex to curOffset
                const before = text.slice(0, atIndex);
                const after = text.slice(curOffset);
                curNode.textContent = before + after;
                // set new caret position
                const newOffset = before?.length;
                const newRange = document.createRange();
                newRange.setStart(curNode, newOffset);
                newRange.collapse(true);
                sel.removeAllRanges();
                sel.addRange(newRange);
            }
        } else {
            // fallback: just insert mention node at range
        }

        // create mention element
        const span = document.createElement("span");
        span.className = "mention";
        // store id on the element but display @name inside the editor
        span.setAttribute("data-mention-id", user._id);
        span.setAttribute("contenteditable", "false");
        span.innerText = `@${user.Name}`;

        // insert span at caret
        const newRange = sel.getRangeAt(0);
        newRange.insertNode(span);
        // insert a trailing space for convenience
        const space = document.createTextNode("\u00A0");
        span.parentNode.insertBefore(space, span.nextSibling);

        // move caret after the space
        const afterRange = document.createRange();
        afterRange.setStartAfter(space);
        afterRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(afterRange);

        setShowMentions(false);
        editorRef.current.focus();
        setTimeout(updateActiveFormats, 0);
        // navigation removed here so inserting mentions while editing does NOT navigate
    };
    // Emoji insertion: insert plain text node (emoji)
    const insertEmoji = (emoji) => {
        const sel = window.getSelection();
        let range = null;

        // Prefer the last saved range (safe when popover/button steals focus)
        if (lastRangeRef.current) {
            range = lastRangeRef.current.cloneRange();
            try {
                sel.removeAllRanges();
                sel.addRange(range);
            } catch (err) {
                // continue; fallback below
            }
        } else if (sel && sel.rangeCount && editorRef.current.contains(sel.anchorNode)) {
            range = sel.getRangeAt(0);
        }

        if (!range) {
            // fallback: append at end
            editorRef.current.focus();
            const node = document.createTextNode(emoji);
            editorRef.current.appendChild(node);
            const r = document.createRange();
            r.setStartAfter(node);
            r.collapse(true);
            const s = window.getSelection();
            s.removeAllRanges();
            s.addRange(r);
            setShowEmojiPopover(false);
            setTimeout(updateActiveFormats, 0);
            lastRangeRef.current = r.cloneRange();
            return;
        }

        // insert emoji at resolved range
        range.deleteContents();
        const node = document.createTextNode(emoji);
        range.insertNode(node);
        // move caret after emoji
        range.setStartAfter(node);
        range.collapse(true);
        try {
            sel.removeAllRanges();
            sel.addRange(range);
        } catch (err) {
            // ignore
        }
        editorRef.current.focus();
        setShowEmojiPopover(false);
        lastRangeRef.current = range.cloneRange();
        setTimeout(updateActiveFormats, 0);
    };


    // File handling: convert to base64 and store { filename, base64code }
    const handleFilePick = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files?.length) return;
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result.split(",")[1];
                setAttachments((prev) => [
                    ...(Array.isArray(prev) ? prev : []),
                    { filename: file.name, base64code: base64 }
                ]);
            };
            reader.readAsDataURL(file);
        });
        // reset input to allow same file selection later
        e.target.value = "";
    };

    const removeAttachment = (idx) => {
        setAttachments((prev) => prev.filter((_, i) => i !== idx));
    };

    // Preview helpers
    const previewPDF = (base64Data) => {
        // Open PDF in a new window using data URL
        const pdfWindow = window.open();
        if (pdfWindow) {
            pdfWindow.document.write(
                `<iframe width='100%' height='100%' src='data:application/pdf;base64,${base64Data}'></iframe>`
            );
        } else {
            message.error('Unable to open PDF preview window.');
        }
    };

    const previewImage = (base64Data, ext) => {
        setPreviewModal({
            visible: true,
            content: `data:image/${ext};base64,${base64Data}`,
            type: 'image',
        });
    };

    // Main preview handler
    const handleFilePreview = (filename, base64Data) => {
        try {
            const fileName = filename;
            const fileExtension = fileName?.split('.').pop()?.toLowerCase();
            if (!base64Data) {
                message.error('No file data available to preview');
                return;
            }
            switch (fileExtension) {
                case 'pdf':
                    previewPDF(base64Data);
                    break;
                case 'docx':
                case 'doc':
                    message.info('Preview not available for Word files. Please download to view.');
                    break;
                case 'csv':
                    message.info('Preview not available for CSV files. Please download to view.');
                    break;
                case 'jpg':
                case 'jpeg':
                case 'png':
                case 'gif':
                case 'bmp':
                case 'webp':
                    previewImage(base64Data, fileExtension);
                    break;
                default:
                    message.error('Unsupported file type for preview');
                    break;
            }
        } catch (error) {
            const errorMessage = error && typeof error === 'object'
                ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
                : 'Failed to preview file due to an unknown error';
            message.error(errorMessage);
        }
    };

    // Send
    const handleSend = () => {
        const html = editorRef.current?.innerHTML || "";
        const currentAttachments = attachmentsRef.current || [];
        if (html.trim() === "" && currentAttachments?.length === 0) return;
        // Provide attachments array to onSend
        if (onSend) {
            onSend({
                html: html,
                files: editMode ? editAttachments : currentAttachments,
                ...(editMode && { id: editMessageId }), // <-- include m.id here
            });
            if (editMode && onCancel) {
                onCancel();
            }
        }
        // cleanup
        editorRef.current.innerHTML = "";
        setCommentText("");
        setAttachments([]);
        setShowMentions(false);
        setTimeout(updateActiveFormats, 0);
    };

    // keyboard: Enter = send, Shift+Enter = newline. If caret is inside a code block (pre) allow newline.
    const handleKeyDown = (e) => {
        if (e.key !== "Enter") return;

        // If mention dropdown is open and exactly one candidate -> choose it on Enter
        if (showMentions && mentionCandidates?.length === 1 && !e.shiftKey) {
            e.preventDefault();
            insertMention(mentionCandidates[0]);
            return;
        }

        const sel = window.getSelection();
        const anchor = sel && sel.anchorNode;
        const inPre = anchor && nodeHasAncestorTag(anchor, ["pre"]);
        const inList = anchor && nodeHasAncestorTag(anchor, ["li", "ul", "ol"]);

        // Code block: Shift+Enter -> newline inside code; Enter (no Shift) -> exit code block
        if (inPre) {
            if (e.shiftKey) {
                // allow native newline inside <pre>
                return;
            }
            // Enter without Shift -> exit pre/code to a new editable line after it
            if (!isRichTextField) {
                console.log("exiting pre block");
                e.preventDefault();


                let p = anchor;
                while (p && p !== editorRef.current && !(p.nodeType === Node.ELEMENT_NODE && p.nodeName.toLowerCase() === "pre")) {
                    p = p.parentNode;
                }
                if (p && p.parentNode) {
                    const after = document.createElement("div");
                    after.innerHTML = "<br>";
                    p.parentNode.insertBefore(after, p.nextSibling);
                    const r = document.createRange();
                    r.setStart(after, 0);
                    r.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(r);
                    editorRef.current.focus();
                    setTimeout(updateActiveFormats, 0);
                }
                return;
            }
        }


        // Lists: Shift+Enter -> split current list item into two
        if (inList && e.shiftKey) {
            e.preventDefault();

            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return;

            const range = sel.getRangeAt(0);

            // Safely find enclosing <li>
            let node = range.startContainer;
            while (node && node.nodeName.toLowerCase() !== 'li') {
                node = node.parentNode;
            }

            if (node && node.nodeName.toLowerCase() === 'li') {
                const currentLi = node;

                // Split the content of the current <li> at the caret
                const beforeRange = range.cloneRange();
                beforeRange.selectNodeContents(currentLi);
                beforeRange.setEnd(range.startContainer, range.startOffset);
                const beforeText = beforeRange.cloneContents();

                const afterRange = range.cloneRange();
                afterRange.selectNodeContents(currentLi);
                afterRange.setStart(range.startContainer, range.startOffset);
                const afterText = afterRange.cloneContents();

                // Replace current <li> with beforeText
                currentLi.innerHTML = '';
                currentLi.appendChild(beforeText);

                // Create new <li> with afterText
                const newLi = document.createElement('li');
                if (afterText.textContent.trim() === '') {
                    newLi.appendChild(document.createElement('br'));
                } else {
                    newLi.appendChild(afterText);
                }

                currentLi.parentNode.insertBefore(newLi, currentLi.nextSibling);

                // Move caret to start of new <li>
                const newRange = document.createRange();
                newRange.selectNodeContents(newLi);
                newRange.collapse(true);
                sel.removeAllRanges();
                sel.addRange(newRange);

                editorRef.current.focus();
                setTimeout(updateActiveFormats, 0);
                return;
            }

            // fallback (not in list)
            const br = document.createElement('br');
            range.insertNode(br);
            range.setStartAfter(br);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            editorRef.current.focus();
            setTimeout(updateActiveFormats, 0);
            return;
        }




        // Shift+Enter elsewhere -> allow native newline
        if (e.shiftKey) return;

        // Otherwise Enter -> send
        if (!isRichTextField) {
            e.preventDefault();

            handleSend();
        }
    };

    // Cleanup object URLs on unmount
    useEffect(() => {
        // watch selection changes to update toolbar highlight
        document.addEventListener("selectionchange", updateActiveFormats);
        return () => {
            attachments.forEach((a) => a.url && URL.revokeObjectURL(a.url));
            document.removeEventListener("selectionchange", updateActiveFormats);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle paste event for files/images
    const handlePaste = (e) => {
        if (!e.clipboardData) return;
        const items = Array.from(e.clipboardData.items || []);
        let found = false;
        items.forEach(item => {
            if (item.kind === 'file' && !isRichTextField) {
                const file = item.getAsFile();
                if (file) {
                    found = true;
                    const reader = new FileReader();
                    reader.onload = () => {
                        const base64 = reader.result.split(',')[1];
                        setAttachments(prev => [
                            ...(Array.isArray(prev) ? prev : []),
                            { filename: file.name || (file.type.startsWith('image/') ? 'pasted-image.png' : 'pasted-file'), base64code: base64 }
                        ]);
                    };
                    reader.readAsDataURL(file);
                }
            }
        });
        if (found) {
            e.preventDefault();
            message.success('File pasted and attached!');
        }
    };

    return (
        <div className="chat-editor-wrapper" ref={wrapperRef}>
            {/* Top toolbar */}
            {showEditors && (
                <div className="top-toolbar">
                    <div className="toolbar-left">
                        <Tooltip title="Bold"><Button className={`toolbar-btn ${activeFormats.bold ? "active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={applyBold} icon={<BoldOutlined />} type="text" /></Tooltip>
                        <Tooltip title="Italic"><Button className={`toolbar-btn ${activeFormats.italic ? "active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={applyItalic} icon={<ItalicOutlined />} type="text" /></Tooltip>
                        <Tooltip title="Strikethrough"><Button className={`toolbar-btn ${activeFormats.strike ? "active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={applyStrike} icon={<StrikethroughOutlined />} type="text" /></Tooltip>
                        <Tooltip title="Link"><Button className={`toolbar-btn`} onMouseDown={(e) => e.preventDefault()} onClick={insertLink} icon={<LinkOutlined />} type="text" /></Tooltip>
                        <Tooltip title="Bulleted list"><Button className={`toolbar-btn ${activeFormats.unordered ? "active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={applyUnordered} icon={<UnorderedListOutlined />} type="text" /></Tooltip>
                        <Tooltip title="Numbered list"><Button className={`toolbar-btn ${activeFormats.ordered ? "active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={applyOrdered} icon={<OrderedListOutlined />} type="text" /></Tooltip>
                        <Tooltip title="Inline code"><Button className={`toolbar-btn ${activeFormats.code ? "active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={applyCode} icon={<CodeOutlined />} type="text" /></Tooltip>
                        <Tooltip title="Quote"><Button className={`toolbar-btn ${activeFormats.quote ? "active" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={applyQuote} type="text" /></Tooltip>
                    </div>
                </div>
            )}

            {/* Editor */}

            <div
                ref={editorRef}
                className={`chat-editor ${focused ? "focused" : ""}`}
                contentEditable
                suppressContentEditableWarning
                placeholder={placeholder}
                onInput={handleInput}
                onFocus={() => {
                    setFocused(true);
                    setShowEditors(true);
                    // ensure toolbar reflects current caret/format when editor is focused
                    setTimeout(updateActiveFormats, 0);
                }}
                onBlur={() => {
                    // small delay so mention clicks register (if clicking mention)
                    setTimeout(() => {
                        setShowMentions(false);
                        setFocused(false);
                    }, 150);
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
            ></div>


            {/* Mention dropdown */}
            {showMentions && mentionCandidates?.length > 0 && (
                <div
                    className="mention-dropdown"
                    style={{ top: dropdownPos.top, left: dropdownPos.left }}
                >
                    {mentionCandidates.map((u) => (
                        <div
                            key={u._id}
                            className="mention-item"
                            onMouseDown={(e) => {
                                // prevent blur
                                e.preventDefault();
                            }}
                            onClick={() => insertMention(u)}
                        >
                            {u?.image ? (
                                <img
                                    src={`data:image/jpeg;base64,${u.image}`}
                                    alt={u.Name || "User"}
                                    style={{ width: 24, height: 24, borderRadius: 13, marginRight: 8, objectFit: "cover" }}
                                />
                            ) : (
                                <Avatar size="small" style={{ backgroundColor: "#87d068", marginRight: 8 }}>
                                    {u?.Name?.charAt(0).toUpperCase()}
                                </Avatar>
                            )}
                            <div className="mention-name">{u.Name}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Attachment previews */}
            {attachments?.length > 0 && (
                <div className="attachment-preview">
                    {attachments?.map((a, i) => {
                        const ext = a.filename.split('.').pop().toLowerCase();
                        const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);
                        return (
                            <div className="attachment-item" key={i}>
                                <div
                                    className="attachment-thumb-wrapper"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleFilePreview(a.filename, a.base64code)}
                                    title="Click to preview"
                                >
                                    {isImage ? (
                                        <img
                                            src={`data:image/${ext};base64,${a.base64code}`}
                                            alt={a.filename}
                                            className="attachment-thumb"
                                        />
                                    ) : (
                                        <div className="attachment-file">
                                            <PaperClipOutlined />{' '}
                                            <span
                                                className="filename"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleFilePreview(a.filename, a.base64code);
                                                }}
                                                style={{ textDecoration: 'underline', color: '#1677ff', cursor: 'pointer' }}
                                            >
                                                {a.filename}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeAttachment(i)}
                                    type="text"
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Image Preview Modal */}
            <Modal
                open={previewModal.visible}
                footer={null}
                onCancel={() => setPreviewModal({ visible: false, content: null, type: null })}
                width={600}
                bodyStyle={{ textAlign: 'center' }}
                destroyOnClose
                getContainer={() => document.body}
                centered
            >
                {previewModal.type === 'image' && (
                    <img src={previewModal.content} alt="Preview" style={{ maxWidth: '100%', maxHeight: 500 }} />
                )}
            </Modal>

            {/* Bottom actions */}
            {showEditors && !isRichTextField && (
                <div className="editor-actions">
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Popover
                            content={
                                <div className="emoji-grid">
                                    {emojis.map((em) => (
                                        <div
                                            key={em}
                                            className="emoji-item"
                                            onMouseDown={(e) => {
                                                // prevent blur and use saved range for insertion
                                                e.preventDefault();
                                                insertEmoji(em);
                                            }}
                                        >
                                            {em}
                                        </div>
                                    ))}
                                </div>
                            }
                            trigger="click"
                            placement="top"
                            visible={showEmojiPopover}
                            onVisibleChange={(v) => setShowEmojiPopover(v)}
                            getPopupContainer={() => wrapperRef.current}
                        >
                            <Button type="text" icon={<SmileOutlined />} />
                        </Popover>

                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: "none" }}
                            multiple
                            onChange={handleFilePick}
                            accept="*/*"
                            disabled={editMode}
                        />
                        <Button
                            type="text"
                            icon={<PaperClipOutlined />}
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            disabled={editMode}
                        />
                    </div>

                    <div>
                        {editMode ? (
                            <>
                                <Button
                                    onClick={onCancel}
                                    style={{ marginRight: 8 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleSend}
                                    disabled={!(commentText.trim()?.length > 0 || attachments?.length > 0)}
                                >
                                    Save
                                </Button>
                            </>
                        ) : (
                            <>
                                {!showTools && (
                                    <Button
                                        onClick={() => {
                                            // reset editor and hide it
                                            editorRef.current.innerHTML = "";
                                            setCommentText("");
                                            setAttachments([]);
                                            setShowEditors(false);
                                        }}
                                        style={{ marginRight: 8 }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={handleSend}
                                    disabled={!(commentText.trim().length > 0 || attachments.length > 0)}
                                >
                                    Send
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
